import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { motion } from 'motion/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { showRelevancyAtom } from '@/atoms/adminBar';
import { DownloadHistoryIcon } from '@/components/Icons/DownloadHistoryIcon';
import { PremiumStarWithTooltip } from '@/components/Icons/PremiumStarWithTooltip';
import MGCompatibilityDisplay from '@/components/MGCompatibilityDisplay';
import ConfirmDownloadModal from '@/components/Modals/ConfirmDownload';
import Tooltip from '@/components/Tooltip';
import { TrendingIconStatic } from '@/components/TrendingIcon';
import { BoardsDrawer } from '@/features/Boards/drawer';
import { BoardsTrigger } from '@/features/Boards/trigger';
import { useBoardDragSource } from '@/features/Boards/sidebar-drag';
import { useDownloads } from '@/hooks/markers/useDownloads';
import { useMGTrending } from '@/hooks/markers/useMGTrending';
import { useIsAdmin, useIsLoggedIn } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { useMouseRest } from '@/hooks/ui/useMouseRest';
import { getCategoryItemFromUrl } from '@/lib/getCategoryFromUrl';
import { cn, isAssetNew } from '@/lib/utils';
import { logEvent } from '@/pages/api/v1/utils';
import { ASSET_TYPE } from '@/types/asset';
import type { MGCompatibility } from '@music-vine/setlist';
import { trackInternalEvent } from '@/utils/tracking';
import { Badge } from '@music-vine/cadence/ui';
import trackPlay from './api/trackPlay';
import { VideoPreview } from './components/VideoPreview';
import { useMGObserver } from './hooks/useMGObserver';
export interface MGTileProps {
  compatibility: MGCompatibility;
  fileSize: string;
  hideCompatibility?: boolean;
  id: string;
  image: string;
  isLut?: boolean;
  meta: {
    contributor: string;
    setLiveUnixTimestamp: number;
    resolution: string;
    isPremium: boolean;
    compatibility: MGCompatibility;
    onAlpha: boolean;
    isFeatured?: boolean;
    relevanceFree?: number;
    relevancePremium?: number;
    downloadCountFree?: number;
    downloadCountPremium?: number;
    playCountFree?: number;
    playCountPremium?: number;
  };
  mgSlug: string;
  title: string;
  video: string;
}
const paddingClass = 'p-3';
export const MGTile = ({
  title,
  image,
  video,
  meta,
  id,
  mgSlug,
  fileSize,
  compatibility,
  isLut = false,
  hideCompatibility = false
}: MGTileProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isBoardsOpen, setIsBoardsOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const intersectionRef = useRef<HTMLDivElement | null>(null);
  const {
    shouldShowUI,
    handlers
  } = useMouseRest({
    enabled: isHovered && window.matchMedia('(min-width: 768px)').matches,
    hasFocus: isFocused
  });
  const {
    isVisible,
    hasProvider
  } = useMGObserver(intersectionRef as React.RefObject<HTMLElement>, id);
  const isLoggedIn = useIsLoggedIn();
  const isAdmin = useIsAdmin();
  const {
    settings
  } = useSettings();
  const isPremiumUser = !!settings?.isPremium;
  const showRelevancy = useAtomValue(showRelevancyAtom);
  const {
    pathname,
    asPath
  } = useRouter();
  const {
    isDownloaded
  } = useDownloads({
    id: Number(id),
    type: 'motion'
  });
  const {
    isTrending
  } = useMGTrending(id);
  const isActive = !(isDownloadModalOpen || isBoardsOpen) && (isHovered && window.matchMedia('(min-width: 768px)').matches || isFocused || isVisible);
  if (isActive && !showVideo) {
    setShowVideo(true);
  }
  if (!isActive) {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setShowVideo(false);
    }, 350);
  }
  useEffect(() => {
    const timeout = setTimeout(async () => {
      const categoryItem = getCategoryItemFromUrl(pathname, isLut);
      try {
        await Promise.all([logEvent({
          event_type: 'play',
          asPath,
          metadata: {
            asset_type: ASSET_TYPE.MOTION,
            asset_id: Number(id)
          }
        }), trackPlay({
          slug: categoryItem ?? mgSlug,
          assetId: id
        }), trackInternalEvent({
          type: 'play'
        })]);
      } catch (e) {
        console.warn(e);
      }
    }, 2500);
    if (!(showVideo && isLoggedIn)) {
      clearTimeout(timeout);
    }
    return () => clearTimeout(timeout);
  }, [id, mgSlug, pathname, showVideo, isLoggedIn, asPath, isLut]);
  const isUsingPremiumRelevance = meta.isPremium || isPremiumUser;

  // Create the asset object with is_new calculated
  const assetForDownload = useMemo(() => {
    const isNew = isAssetNew(meta.setLiveUnixTimestamp);
    return {
      type: 'motion' as const,
      data: {
        id,
        slug: mgSlug,
        name: title,
        is_premium: meta.isPremium,
        image,
        contributor: {
          name: meta.contributor
        },
        is_lut: isLut,
        file_size: fileSize,
        compatibility,
        compatibilityNode: <MGCompatibilityDisplay compatibility={meta.compatibility} />,
        is_new: isNew
      }
    };
  }, [id, mgSlug, title, meta.isPremium, meta.contributor, meta.setLiveUnixTimestamp, meta.compatibility, image, isLut, fileSize, compatibility]);
  const boardDragProps = useBoardDragSource(assetForDownload);
  return <div {...boardDragProps} className={cn('group relative aspect-video w-full overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-900', !shouldShowUI && 'md:cursor-none')} onBlur={() => {
    setIsFocused(false);
    setIsHovered(false);
  }} onFocus={() => setIsFocused(true)} onMouseEnter={e => {
    if (window.matchMedia('(min-width: 768px)').matches) {
      setIsHovered(true);
      handlers.onMouseEnter(e);
    }
  }} onMouseLeave={e => {
    if (window.matchMedia('(min-width: 768px)').matches) {
      setIsHovered(false);
      handlers.onMouseLeave(e);
    }
  }} onMouseMove={e => {
    if (window.matchMedia('(min-width: 768px)').matches) {
      handlers.onMouseMove(e);
    }
  }} ref={hasProvider ? intersectionRef : null} data-sentry-component="MGTile" data-sentry-source-file="index.tsx">
      {isAdmin && showRelevancy && <div className="absolute top-0 left-0 z-50 space-y-2 rounded-br-lg bg-black/80 p-2 font-mono text-white text-xs transition-colors group-hover:bg-black/90">
          {meta.isFeatured !== undefined && <div className="flex items-center gap-1.5">
              <span className={cn('size-2 rounded-full', meta.isFeatured ? 'bg-green-500' : 'bg-gray-500')} />
              <span className="font-semibold">
                Featured: {meta.isFeatured ? 'Yes' : 'No'}
              </span>
            </div>}
          {(meta.relevanceFree !== undefined || meta.downloadCountFree !== undefined || meta.playCountFree !== undefined) && <div className={cn('space-y-1', {
        underline: !isUsingPremiumRelevance,
        'opacity-75 transition-opacity group-hover:opacity-100': isUsingPremiumRelevance
      })}>
              <div className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-free" />
                <span className="font-semibold">Free</span>
              </div>
              {meta.relevanceFree !== undefined && <div className="pl-3.5">
                  Score: {meta.relevanceFree.toFixed(2)}
                </div>}
              {meta.downloadCountFree !== undefined && <div className="pl-3.5">
                  Downloads: {meta.downloadCountFree}
                </div>}
              {meta.playCountFree !== undefined && <div className="pl-3.5">Plays: {meta.playCountFree}</div>}
            </div>}
          {(meta.relevancePremium !== undefined || meta.downloadCountPremium !== undefined || meta.playCountPremium !== undefined) && <div className={cn('space-y-1', {
        underline: isUsingPremiumRelevance,
        'opacity-75 transition-opacity group-hover:opacity-100': !isUsingPremiumRelevance
      })}>
              <div className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-pink-500" />
                <span className="font-semibold">Premium</span>
              </div>
              {meta.relevancePremium !== undefined && <div className="pl-3.5">
                  Score: {meta.relevancePremium.toFixed(2)}
                </div>}
              {meta.downloadCountPremium !== undefined && <div className="pl-3.5">
                  Downloads: {meta.downloadCountPremium}
                </div>}
              {meta.playCountPremium !== undefined && <div className="pl-3.5">Plays: {meta.playCountPremium}</div>}
            </div>}
        </div>}
      <div className="relative h-full w-full">
        <VideoPreview alt={title} imageUrl={image} isPlaying={isActive} showVideo={showVideo} src={video} data-sentry-element="VideoPreview" data-sentry-source-file="index.tsx" />
      </div>
      <motion.div animate={{
      opacity: isActive && shouldShowUI ? 1 : 0,
      scale: isActive && shouldShowUI ? 1 : 1.01
    }} className={cn('absolute inset-0 flex flex-col justify-between will-change-transform', 'group-focus-within:scale-100 group-hover:scale-100', 'group-data-[state=info-open]:scale-100 group-data-[state=info-open]:opacity-100')} initial={{
      opacity: 0,
      scale: 1.01
    }} transition={{
      ease: 'easeOut',
      duration: 0.2
    }} data-sentry-element="motion.div" data-sentry-source-file="index.tsx">
        <Link aria-label={`View the ${title} asset`} className="absolute inset-0 z-10 rounded-lg transition focus:outline-hidden focus-visible:border-2 focus-visible:border-pink-500 focus-visible:border-solid" data-testid="mg-tile-link" href={`${isLut ? '/luts' : '/motion-graphics'}/asset/${mgSlug}`} data-sentry-element="Link" data-sentry-source-file="index.tsx" />
        <div className="pointer-events-none absolute inset-x-0 -bottom-1 h-full bg-gradient-to-t from-black/70 to-45%" />
        {!hideCompatibility && <div className={clsx('pointer-events-none absolute top-0 left-0 z-50 flex w-full flex-row justify-between gap-2', paddingClass)}>
            <MGCompatibilityDisplay className="rounded-lg border-1 border-gray-150 border-solid bg-white/95 px-2 py-0.5 [&>svg]:size-4" compatibility={meta.compatibility} size="sm" />
          </div>}
        <div className={clsx('pointer-events-none absolute bottom-0 left-0 z-10 flex w-full flex-row justify-between gap-2', paddingClass)}>
          <div className="flex w-full flex-row items-end gap-2">
            <div className="flex w-full flex-1 items-center gap-3 self-end pr-24">
              <h3 className="flex-1 truncate font-sans font-semibold text-base text-white drop-shadow" data-testid="mg-tile-title">
                {title}
              </h3>
            </div>
          </div>
        </div>
      </motion.div>
      <div className="absolute right-3 bottom-3 flex items-end gap-3">
        <div className={cn('transition-opacity duration-200', isActive && shouldShowUI ? 'opacity-100' : 'opacity-0 hover:opacity-100')} onMouseDown={e => {
        e.preventDefault();
        setIsHovered(false);
        setIsFocused(false);
      }}>
          <ConfirmDownloadModal asset={assetForDownload} buttonVariant="icon" className="size-9" onClose={wasKeyboardClose => {
          setIsDownloadModalOpen(false);
          setIsFocused(false);
          setIsHovered(false);
          if (!wasKeyboardClose) {
            (document.activeElement as HTMLElement)?.blur?.();
          }
        }} onOpen={() => setIsDownloadModalOpen(true)} premium={meta.isPremium} data-sentry-element="ConfirmDownloadModal" data-sentry-source-file="index.tsx" />
        </div>
        <div className="flex flex-col items-center justify-center gap-1">
          {/* Mobile screens */}
          <span className="inline-flex xs:hidden items-center justify-center">
            <BoardsDrawer asset={assetForDownload} inMgTile onClose={() => {
            setIsBoardsOpen(false);
            setIsFocused(false);
            setIsHovered(false);
          }} onOpen={() => setIsBoardsOpen(true)} data-sentry-element="BoardsDrawer" data-sentry-source-file="index.tsx" />
          </span>

          {/* Desktop screens */}
          <span className="xs:inline-flex hidden items-center justify-center">
            <BoardsTrigger asset={assetForDownload} inMgTile onClose={() => {
            setIsBoardsOpen(false);
            setIsFocused(false);
            setIsHovered(false);
          }} onOpen={() => setIsBoardsOpen(true)} showOnHoverOnly data-sentry-element="BoardsTrigger" data-sentry-source-file="index.tsx" />
          </span>

          {!!isDownloaded && <DownloadHistoryIcon className="pointer-events-none size-5 text-gray-50 dark:text-gray-50" />}
        </div>
      </div>
      <div className={cn('absolute top-3 right-3 flex flex-row items-center gap-2')}>
        {isAssetNew(meta.setLiveUnixTimestamp) && <div className={cn('transition-opacity duration-200', shouldShowUI ? 'opacity-100' : 'opacity-0')}>
            <Badge className="whitespace-nowrap rounded-[4px] text-sm lowercase" size="xs" strong variant="brandSecondary">
              New
            </Badge>
          </div>}
        {!!isTrending && <Tooltip label="Trending asset">
            <div className="flex items-center justify-center">
              <TrendingIconStatic className="size-5 rounded-xs" />
            </div>
          </Tooltip>}
        {!!meta.isPremium && <PremiumStarWithTooltip className="size-5 [&_svg>path:last-of-type]:stroke-pink-500 [&_svg>path:last-of-type]:dark:stroke-pink-500" data-testid="premium-star" label="Premium asset" />}
      </div>
    </div>;
};